import { defineStore } from 'pinia';
import { ref, toRaw, nextTick } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useAnswersStore } from '@stores';
import { useI18n } from 'vue-i18n';
import { validateQuestion } from '@utils';
import { useRouter } from 'vue-router';

import { fetchQuestion, getQuestionsFetch, updateQuestionFetch, duplicateQuestionFetch, deleteQuestionFetch, getQuestionFetch } from '@services/testService';
export const useQuestionsStore = defineStore('useQuestionsStore', () => {
    const toast = useToast();
    const answersStore = useAnswersStore();
    const router = useRouter();
    const { t } = useI18n({ useScope: 'global' });

    const currentTestID = ref(null);
    const allQuestions = ref([]);
    const selectedId = ref(null);
    const question = ref({});

    const getQuestion = async (id) => {
        question.value = await getQuestionFetch(id);
    };

    const selectQuestion = async (id) => {
        await nextTick();
        focusSelectedQuestion(id);

        selectedId.value = id;
        await getQuestion(id);

        router.push({ query: { question: id } });
    };

    const focusSelectedQuestion = async (id) => {
        const questionElement = document.querySelector(`[data-id="${id}"]`);
        if (questionElement) {
            questionElement.focus();
            questionElement.scrollIntoView({
                behavior: 'smooth', // Плавный скролл
                block: 'nearest', // Прокручивает так, чтобы элемент был виден максимально удобно
            });
        }
    };

    const addQuestion = async () => {
        const errorMessage = validateQuestion(question.value.type, question.value.answers);

        if (errorMessage) {
            toast.add({ severity: 'error', detail: t(errorMessage), life: 3000 });
            return;
        }

        const newQuestion = {
            type: 1,
            isMultiple: false,
            question_id: null,
            order: allQuestions.value.length + 1,
            titles: [],
            answers: [],
            id: currentTestID.value,
        };

        const questionId = await fetchQuestion(newQuestion);
        const isQuestionExist = allQuestions.value?.some((q) => q.question_id === questionId);
        if (isQuestionExist) {
            toast.add({ severity: 'error', detail: t('tests.questionError'), life: 3000 });
            await getQuestions(currentTestID.value);
            await selectQuestion(questionId);
            return;
        }

        allQuestions.value.push({ ...newQuestion, question_id: questionId });
        await selectQuestion(questionId);

        if (!question.value.answers?.length) {
            await answersStore.addAnswer();
        }
    };

    const updateQuestionOrder = async (index) => {
        const draggedQuestion = { ...allQuestions.value[index], order: index + 1 };
        try {
            await updateQuestionFetch(draggedQuestion);
            allQuestions.value.forEach((q, i) => (q.order = i + 1));
            selectQuestion(draggedQuestion.question_id);
        } catch (e) {
            toast.add({ severity: 'error', summary: t('form.error'), detail: t('form.errors.' + e.response.data.message), life: 3000 });
        }
    };

    const updateQuestion = async (value) => {
        const updatedQuestion = { ...question.value, ...value };
        try {
            await updateQuestionFetch(updatedQuestion);
        } catch (e) {
            toast.add({ severity: 'error', summary: t('form.error'), detail: t('form.errors.' + e.response.data.message), life: 3000 });
        }
    };

    const getQuestions = async (id) => {
        currentTestID.value = id;
        allQuestions.value = await getQuestionsFetch(id);
    };

    const deleteQuestion = async (question) => {
        const { question_id } = question;

        try {
            await deleteQuestionFetch({ question_id });
            allQuestions.value = allQuestions.value.filter((q) => q.question_id !== question_id);
            if (allQuestions.value.length) {
                selectQuestion(allQuestions.value[0].question_id);
            }
        } catch (e) {
            toast.add({ severity: 'error', summary: t('form.error'), detail: t('form.errors.' + e.response.data.message), life: 3000 });
        }
    };

    const duplicateQuestion = async (question) => {
        const { question_id } = question;
        const copy = JSON.parse(JSON.stringify(toRaw(question)));

        try {
            const data = await duplicateQuestionFetch({ question_id });
            if (question_id === data) {
                selectQuestion(data);
            } else {
                allQuestions.value.push({ ...copy, question_id: data });
                selectQuestion(data);
            }
        } catch (e) {
            toast.add({ severity: 'error', summary: t('form.error'), detail: t('form.errors.' + e.response.data.message), life: 3000 });
        }
    };

    return {
        allQuestions,
        selectedId,
        addQuestion,
        selectQuestion,
        updateQuestionOrder,
        getQuestions,
        getQuestion,
        duplicateQuestion,
        deleteQuestion,
        updateQuestion,
        question,
    };
});
